/**
 * useFeedback Hook
 *
 * React Query hooks for fetching and managing AI-generated feedback for
 * reflections. Includes hooks for fetching feedback and triggering
 * regeneration.
 *
 * Polling model: the GET feedback response carries a
 * `regeneration_in_progress` flag derived server-side from
 * `regeneration_started_at` (10-minute staleness window). The
 * `useFeedback` query auto-refetches every 2 seconds while that flag is
 * true, so the UI recovers without any manual `setInterval`/`setTimeout`
 * dance. Mirrors the speaker-memory regeneration fix shipped in PR #140.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getReflectionFeedback,
  regenerateFeedback,
} from "../services/feedbackService";
import type { ReflectionFeedbackResponse } from "../types/reflection";

/**
 * Hook to fetch feedback for a specific reflection
 *
 * Only fetches when reflectionId is provided and the reflection is complete.
 * While `regeneration_in_progress` is true on the returned row, refetches
 * every 2 seconds to drive the spinner UI.
 */
export function useFeedback(reflectionId: string | undefined, enabled = true) {
  return useQuery<ReflectionFeedbackResponse, Error>({
    queryKey: ["feedback", reflectionId],
    queryFn: () => getReflectionFeedback(reflectionId!),
    enabled: !!reflectionId && enabled,
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      // Don't retry on 404 (feedback not yet generated)
      if (error.message.includes("404")) return false;
      return failureCount < 2;
    },
    refetchInterval: (query) =>
      query.state.data?.regeneration_in_progress ? 2000 : false,
  });
}

/**
 * Hook to regenerate feedback for a specific reflection
 *
 * Returns a mutation that enqueues an async regeneration task. The
 * server responds with the stamped `ReflectionFeedbackResponse`; the
 * polling query on `useFeedback` takes over from there.
 */
export function useRegenerateFeedback(reflectionId: string) {
  const queryClient = useQueryClient();

  return useMutation<ReflectionFeedbackResponse, Error, string | undefined>({
    mutationFn: (therapyStyleId?: string) =>
      regenerateFeedback(reflectionId, therapyStyleId),
    onSuccess: (data) => {
      // Prime the cache with the stamped row so the polling query sees
      // `regeneration_in_progress=true` on the very next tick.
      queryClient.setQueryData(["feedback", reflectionId], data);
      queryClient.invalidateQueries({ queryKey: ["feedback", reflectionId] });
    },
  });
}
